import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { Button } from '../ui/Button';
import { refreshUntilShown } from '../ui/QueryStates';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from './sendOnce';

/**
 * Follow / Unfollow, in the panel's header: the bell hears only about what you follow. The
 * label names the action it takes (no `aria-pressed` beside a changing label: one or the
 * other).
 */
export function FollowButton({
  ticketId,
  following,
}: {
  ticketId: string;
  following: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const shows = { queryKey: trpc.ticket.get.queryKey({ ticketId }) };
  const options = {
    onSuccess: () => refreshUntilShown(qc, shows),
    onSettled: () => lock.release(),
  };
  const follow = useMutation(trpc.ticket.follow.mutationOptions(options));
  const unfollow = useMutation(trpc.ticket.unfollow.mutationOptions(options));
  const busy = follow.isPending || unfollow.isPending;
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-disabled={busy || undefined}
      onClick={() =>
        sendOnce(lock, following ? unfollow.mutate : follow.mutate, { ticketId })
      }
    >
      {following ? 'Unfollow' : 'Follow'}
    </Button>
  );
}
